import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { Switch } from "@clockwise/design-system";
import { FlexRange } from "@clockwise/schema/v2";
import { EventTableRow } from "@clockwise/web-commons/src/components/EventTableNew";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { AttendeeAvatarStack } from "@clockwise/web-commons/src/ui/AttendeeAvatarStack";
import { Tooltip } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { noop, range } from "lodash";
import React from "react";
import { AttendeeForFlexEvent } from "../util/parseFlexibility";
import { FlexEventMenu } from "./FlexEventMenu";

export const Async = ({
  loadingTypes,
  loading,
  rowCount = 3,
  children,
}: {
  loadingTypes: ("text" | "avatar")[];
  loading: boolean;
  rowCount?: number;
  children: React.ReactNode;
}) => {
  return loading ? (
    range(rowCount).map((_, index) => (
      <EventTableRow key={`row-loading-${index}`}>
        {loadingTypes.map((type, index) => {
          if (type === "text") {
            return <Skeleton key={`loading-table-${index}`} variant="text" width="8rem" />;
          } else {
            return (
              <div key={`loading-table-${index}`} className="cw-flex cw-items-center">
                <Skeleton variant="circle" width="20px" height="20px" />
                <Skeleton variant="circle" width="20px" height="20px" />
                <Skeleton variant="circle" width="20px" height="20px" />
              </div>
            );
          }
        })}
      </EventTableRow>
    ))
  ) : (
    <>{children}</>
  );
};

export const FlexEventRow = ({
  attendees = [],
  eventId = "",
  flexible = false,
  flexRange = FlexRange.Day,
  onFlexibilityChange = noop,
  openFlexSettings = noop,
  recurrenceText,
  title = "Untitled Event",
  updating = false,
  showAttendees = false,
}: {
  attendees?: AttendeeForFlexEvent[];
  eventId?: string;
  flexible?: boolean;
  flexRange?: FlexRange;
  onFlexibilityChange?: (eventId: string, isFlexible: boolean, flexRange: FlexRange) => void;
  openFlexSettings?: (eventId: string) => void;
  recurrenceText?: string;
  title?: string;
  updating?: boolean;
  showAttendees?: boolean;
}) => {
  const translation = React.useContext(TranslationContext);
  const tooltipText = translation("autopilot-form:toggle-tooltip", { on: !!flexible });

  const handleFlexibilityChange = (isFlexible: boolean) => {
    onFlexibilityChange(eventId, isFlexible, flexRange);
  };

  const handleFlexibilityRangeChange = (newRange: FlexRange) =>
    onFlexibilityChange(eventId, flexible, newRange);

  return (
    <EventTableRow>
      <div className="cw-font-semibold">{title}</div>
      {showAttendees && (
        <>
          {attendees ? (
            <AttendeeAvatarStack size="large" overlap maxShown={3}>
              {attendees.map((attendee, index) => {
                return <AttendeeAvatar key={index} profile={attendee.person} size="large" />;
              })}
            </AttendeeAvatarStack>
          ) : (
            <div>meeting has no attendees</div>
          )}
        </>
      )}
      <div className="cw-font-light">{recurrenceText || "Never"}</div>
      <div className="cw-flex cw-gap-3">
        <Tooltip title={tooltipText}>
          <Switch
            checked={flexible}
            disabled={updating}
            onChange={handleFlexibilityChange}
            size="small"
          />
        </Tooltip>
        {flexible ? (
          <FlexEventMenu
            flexRange={flexRange}
            openFlexSettings={() => {
              openFlexSettings(eventId);
            }}
            handleFlexRangeChange={handleFlexibilityRangeChange}
          />
        ) : (
          <span className="cw-text-muted cw-font-medium cw-text-13">Mark as Flexible</span>
        )}
      </div>
    </EventTableRow>
  );
};
